import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Separator } from "../ui/separator";
import { 
  Brain, 
  TrendingUp, 
  Shield, 
  Zap, 
  Target, 
  BarChart3, 
  AlertTriangle, 
  Lightbulb, 
  Users, 
  Lock, 
  CheckCircle, 
  ArrowRight,
  Eye,
  Settings,
  PieChart,
  Activity,
  Database,
  Search,
  Workflow
} from "lucide-react";

export function NexAI() {
  const aiCapabilities = [
    {
      icon: TrendingUp,
      title: "Spend Forecasting & Optimization",
      description: "Predict demand and suggest procurement quantities and timing based on historical data and market trends."
    },
    {
      icon: PieChart,
      title: "Scheme ROI Prediction",
      description: "Estimate the financial impact of promotional schemes before launch with advanced modeling."
    },
    {
      icon: AlertTriangle,
      title: "Anomaly Detection",
      description: "Automatically detect price mismatches, duplicate claims, or outlier vendor bids in real-time."
    },
    {
      icon: Lightbulb,
      title: "Auto-Recommendations",
      description: "Suggest scheme structures, auction formats, or vendor selections based on historical success patterns."
    },
    {
      icon: Search,
      title: "Pattern Recognition",
      description: "Learn from past behavior to suggest optimal rules, timelines, and negotiation strategies."
    }
  ];

  const useCases = [
    {
      module: "NexProcure",
      useCase: "Forecasting purchase quantities and pricing windows based on consumption patterns",
      color: "bg-primary/10 text-primary"
    },
    {
      module: "NexScheme",
      useCase: "Predicting payout impact and ROI before scheme approval",
      color: "bg-secondary/10 text-secondary"
    },
    {
      module: "NexAuction",
      useCase: "Detecting vendor collusion or bid pattern irregularities",
      color: "bg-accent/10 text-accent"
    },
    {
      module: "NexReconcile",
      useCase: "Auto-matching and flagging mismatches in vendor payouts",
      color: "bg-muted/50 text-muted-foreground"
    }
  ];

  const engineFeatures = [
    {
      icon: Brain,
      title: "Self-Learning Model",
      description: "Improves accuracy as usage increases through continuous learning algorithms"
    },
    {
      icon: Zap,
      title: "Plug-and-Play",
      description: "Pre-integrated with all Nex modules for seamless deployment"
    },
    {
      icon: Eye,
      title: "Explainable AI",
      description: "Transparent decision logic and insights you can understand and trust"
    },
    {
      icon: Settings,
      title: "Configurable Thresholds",
      description: "Fine-tune prediction tolerance to match your business requirements"
    },
    {
      icon: Workflow,
      title: "Scalable Across Categories",
      description: "Works across raw materials, services, capex, and trade promotions"
    }
  ];

  const benefits = [
    { value: "25–40%", label: "Fewer disputes in payout cycles", description: "Reduced manual intervention" },
    { value: "20%", label: "More accurate procurement planning", description: "Enhanced forecasting precision" },
    { value: "3x", label: "Faster scheme ROI analysis", description: "Accelerated decision making" },
    { value: "90%", label: "Reduction in manual fraud checks", description: "Automated detection systems" }
  ];

  const complianceFeatures = [
    {
      icon: Shield,
      title: "GDPR & Data Protection Compliance",
      description: "Full compliance with global data protection regulations"
    },
    {
      icon: Lock,
      title: "On-Prem or Secure Cloud Hosting",
      description: "Flexible deployment options to meet your security requirements"
    },
    {
      icon: Users,
      title: "Role-Based AI Visibility",
      description: "Granular control over AI insights and recommendations by user role"
    },
    {
      icon: CheckCircle,
      title: "Ethical, Bias-Checked Models",
      description: "AI models tested and validated for fairness and ethical decision-making"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* What is NexAI? Section */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <Badge variant="secondary" className="mb-6">
              <Brain className="mr-1 h-3 w-3" />
              What is NexAI?
            </Badge>
            
            <p className="mb-8 text-lg text-muted-foreground lg:text-xl leading-relaxed">
              NexAI is the embedded AI engine within NexProcureAI. It works behind the scenes to predict, detect, suggest, and optimize decisions across procurement, schemes, auctions, and reconciliation—turning your data into actionable intelligence.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full" style={{ backgroundColor: '#819A40' }}>
                  <Brain className="h-6 w-6 text-white" />
                </div>
                <h3 className="mb-2">Adaptive Learning Engine</h3>
              </div>
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-secondary">
                  <Workflow className="h-6 w-6 text-white" />
                </div>
                <h3 className="mb-2">AI-Enabled Automation</h3>
              </div>
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary">
                  <TrendingUp className="h-6 w-6 text-white" />
                </div>
                <h3 className="mb-2">ROI & Risk Optimization</h3>
              </div>
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full" style={{ backgroundColor: '#819A40' }}>
                  <Target className="h-6 w-6 text-white" />
                </div>
                <h3 className="mb-2">Integrated Across Modules</h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key AI Capabilities */}
      <section className="py-16 lg:py-24 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="mb-4 text-3xl lg:text-4xl">What NexAI Can Do for You</h2>
              <p className="text-lg text-muted-foreground">
                Powerful AI capabilities designed to transform your procurement and scheme management operations
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {aiCapabilities.map((capability, index) => (
                <Card key={index} className="h-full hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                      <capability.icon className="h-6 w-6 text-primary" />
                    </div>
                    <CardTitle className="text-lg">{capability.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{capability.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Across Modules */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="mb-4 text-3xl lg:text-4xl">AI That Works Across Every Nex Module</h2>
              <p className="text-lg text-muted-foreground">
                See how NexAI integrates seamlessly across all your procurement workflows
              </p>
            </div>

            <div className="space-y-6">
              {useCases.map((useCase, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center gap-4">
                      <div className="flex-shrink-0">
                        <Badge className={`px-3 py-1 ${useCase.color}`}>
                          {useCase.module}
                        </Badge>
                      </div>
                      <div className="flex-grow">
                        <p className="text-muted-foreground">{useCase.useCase}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Insights & Analytics Dashboards */}
      <section className="py-16 lg:py-24" style={{ backgroundColor: '#819A40' }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="mb-6 text-3xl lg:text-4xl text-white">From Raw Data to Real-Time Decisions</h2>
            <p className="mb-12 text-lg text-white/90">
              Transform complex data streams into actionable insights with intelligent dashboards and predictive analytics
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Card className="bg-white/10 border-white/20 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    AI-Generated Procurement Trends
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-white/80">Real-time analytics showing market patterns and optimization opportunities</p>
                </CardContent>
              </Card>

              <Card className="bg-white/10 border-white/20 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Target className="h-5 w-5" />
                    Scheme Success Predictors
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-white/80">Predictive models that forecast promotional campaign performance</p>
                </CardContent>
              </Card>

              <Card className="bg-white/10 border-white/20 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5" />
                    Smart Alerts
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-white/80">"High-value scheme running below ROI threshold" - Proactive notifications</p>
                </CardContent>
              </Card>

              <Card className="bg-white/10 border-white/20 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Activity className="h-5 w-5" />
                    Predictive Dashboards
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-white/80">Interactive dashboards with drill-down capabilities and trend analysis</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* AI Engine Features */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="mb-4 text-3xl lg:text-4xl">Why NexAI is Different</h2>
              <p className="text-lg text-muted-foreground">
                Built for enterprise-grade performance with features that matter to your business
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {engineFeatures.map((feature, index) => (
                <div key={index} className="text-center">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-secondary/10">
                    <feature.icon className="h-8 w-8 text-secondary" />
                  </div>
                  <h3 className="mb-3 text-xl">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Enterprise Benefits */}
      <section className="py-16 lg:py-24 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="mb-4 text-3xl lg:text-4xl">Unlock Tangible Value with NexAI</h2>
              <p className="text-lg text-muted-foreground">
                Measurable results that impact your bottom line
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, index) => (
                <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-3xl text-primary">{benefit.value}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="font-medium mb-1">{benefit.label}</p>
                    <p className="text-sm text-muted-foreground">{benefit.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Data Privacy & Compliance */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="mb-4 text-3xl lg:text-4xl">Responsible AI. Enterprise Grade.</h2>
              <p className="text-lg text-muted-foreground">
                Security, compliance, and ethical AI practices built into every decision
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {complianceFeatures.map((feature, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full" style={{ backgroundColor: '#819A40' }}>
                        <feature.icon className="h-5 w-5 text-white" />
                      </div>
                      <CardTitle className="text-lg">{feature.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 lg:py-24 bg-secondary">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="mb-6 text-3xl lg:text-4xl text-white">
              Ready to Bring Predictive Intelligence into Procurement?
            </h2>
            <p className="mb-8 text-lg text-white/90">
              NexAI helps you reduce guesswork and make confident, data-backed decisions—at scale.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="group bg-white text-secondary hover:bg-gray-100">
                Talk to an AI Consultant
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button size="lg" className="bg-white text-secondary border-white hover:bg-gray-100">
                <Database className="mr-2 h-4 w-4" />
                Explore NexAI Insights
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}